import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Avatar, Space, Tag, Spin, Empty, Button, Typography, Tooltip, message, Dropdown, Modal } from 'antd';
import {
  UserOutlined, TeamOutlined, ReloadOutlined, FireOutlined,
  NotificationOutlined, TrophyOutlined, CheckCircleOutlined, ClockCircleOutlined,
  HeartOutlined, HeartFilled, MoreOutlined, FlagOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { absoluteUploadUrl } from '../../utils/uploadUrl';

const { Text, Title } = Typography;

// Config visual por tipo de evento — cada tipo tem cor, icone, titulo e um
// contexto proprio (o "subtitle contextual") separado da data que sempre
// aparece embaixo.
const TYPE_CONFIG = {
  new_player: {
    label: 'Novo boleiro',
    tagColor: 'blue',
    avatarColor: '#1890ff',
    icon: <UserOutlined />,
    title: (ev) => ev.title,
    context: (ev) => ev.subtitle || 'Comunidade',
  },
  new_team: {
    label: 'Novo time',
    tagColor: 'green',
    avatarColor: '#25D366',
    icon: <TeamOutlined />,
    title: (ev) => ev.title,
    context: (ev) => ev.subtitle || 'Comunidade',
  },
  call_sent: {
    label: 'Convocação',
    tagColor: 'orange',
    avatarColor: '#fa8c16',
    icon: <NotificationOutlined />,
    title: (ev) => `${ev.title} convocou a galera pro jogo`,
    context: (ev) => ev.badge ? `Jogo ${ev.badge}` : 'Jogo aberto',
  },
  mvp: {
    label: 'MVP',
    tagColor: 'purple',
    avatarColor: '#722ed1',
    icon: <TrophyOutlined />,
    title: (ev) => `${ev.title} foi MVP`,
    context: (ev) => ev.subtitle || 'no jogo',
  },
  invite_accepted: {
    label: 'Entrou pro time',
    tagColor: 'cyan',
    avatarColor: '#13c2c2',
    icon: <CheckCircleOutlined />,
    title: (ev) => `${ev.title} entrou pro ${ev.subtitle || 'time'}`,
    context: () => 'Bora que bora',
  },
  team_news: {
    label: 'Notícia',
    tagColor: 'green',
    avatarColor: '#25D366',
    icon: <NotificationOutlined />,
    title: (ev) => `${ev.title} publicou`,
    context: (ev) => ev.subtitle ? (ev.subtitle.length > 140 ? ev.subtitle.slice(0, 140) + '...' : ev.subtitle) : '',
  },
};

function relativeTime(iso) {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  if (isNaN(diff)) return null;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `há ${d}d`;
  const m = Math.floor(d / 30);
  return `há ${m} ${m > 1 ? 'meses' : 'mês'}`;
}

function absoluteTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function EventCard({ ev, onClick, reaction, onLike, onReport }) {
  const cfg = TYPE_CONFIG[ev.type] || TYPE_CONFIG.new_player;
  const rel = relativeTime(ev.ts);
  const abs = absoluteTime(ev.ts);
  const likes = reaction?.likes || 0;
  const liked = !!reaction?.liked_by_me;
  return (
    <Card size="small" hoverable={!!onClick}
      style={{ marginBottom: 10, cursor: onClick ? 'pointer' : 'default' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div onClick={onClick} style={{ flex: 1, display: 'flex', gap: 12, alignItems: 'center', minWidth: 0 }}>
          <Avatar size={44} src={absoluteUploadUrl(ev.image_url)} icon={cfg.icon}
            style={{ background: ev.image_url ? 'transparent' : cfg.avatarColor }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Space size={6} wrap>
              <Tag color={cfg.tagColor} style={{ margin: 0 }}>{cfg.label}</Tag>
              {ev.type === 'new_player' && ev.badge && <Tag>{ev.badge}</Tag>}
              {ev.type === 'new_team' && ev.badge === 'community' && <Tag color="green">Comunidade</Tag>}
              {ev.type === 'new_team' && ev.badge === 'futezap' && <Tag color="gold">Futezap</Tag>}
            </Space>
            <div style={{ fontWeight: 700, fontSize: 15, marginTop: 2 }}>
              {cfg.title(ev)}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {cfg.context(ev)}
              {rel && abs && (
                <>
                  {' · '}
                  <Tooltip title={abs}>
                    <span><ClockCircleOutlined style={{ fontSize: 10, marginRight: 2 }} />{rel}</span>
                  </Tooltip>
                </>
              )}
            </Text>
          </div>
        </div>
        {/* Coluna direita: curtir + more */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} onClick={e => e.stopPropagation()}>
          <Button type="text" size="small"
            icon={liked ? <HeartFilled style={{ color: '#f5222d' }} /> : <HeartOutlined />}
            onClick={onLike}>
            {likes > 0 ? likes : null}
          </Button>
          <Dropdown trigger={['click']} menu={{ items: [
            { key: 'report', icon: <FlagOutlined />, label: 'Denunciar', danger: true, onClick: onReport },
          ] }}>
            <Button type="text" size="small" icon={<MoreOutlined />} />
          </Dropdown>
        </div>
      </div>
    </Card>
  );
}

// Feed sistema-gerado da comunidade.
// Props:
//   compact: se true, mostra 5 primeiros com CTA "Ver mais" (sem scroll infinito)
//   onVerMais: callback opcional
export default function CommunityFeed({ compact = false, onVerMais }) {
  const nav = useNavigate();
  const [events, setEvents] = useState([]);
  const [reactions, setReactions] = useState({}); // { "type:refId": { likes, liked_by_me } }
  const [loading, setLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [done, setDone] = useState(false);
  const sentinelRef = useRef(null);
  const cursorRef = useRef(null);
  const loadingRef = useRef(false);

  // Fetch bulk das reactions pros events carregados
  const fetchReactions = useCallback(async (evs) => {
    if (!evs?.length) return;
    // Agrupa por type
    const byType = {};
    evs.forEach(e => {
      if (!byType[e.type]) byType[e.type] = [];
      byType[e.type].push(e.ref_id);
    });
    const acc = {};
    await Promise.all(Object.entries(byType).map(async ([type, ids]) => {
      try {
        const r = await api.get('/community/reactions/bulk', { params: { type, ids: ids.join(',') } });
        Object.entries(r.data?.reactions || {}).forEach(([id, data]) => {
          acc[`${type}:${id}`] = data;
        });
      } catch { /* silencioso */ }
    }));
    setReactions(prev => ({ ...prev, ...acc }));
  }, []);

  const toggleLike = async (ev) => {
    const key = `${ev.type}:${ev.ref_id}`;
    const cur = reactions[key] || { likes: 0, liked_by_me: false };
    // Optimistic update
    const nextState = cur.liked_by_me
      ? { likes: Math.max(0, cur.likes - 1), liked_by_me: false }
      : { likes: cur.likes + 1, liked_by_me: true };
    setReactions(prev => ({ ...prev, [key]: nextState }));
    try {
      if (cur.liked_by_me) {
        await api.delete(`/community/reactions/${ev.type}/${ev.ref_id}`);
      } else {
        await api.post('/community/reactions', { event_type: ev.type, event_ref_id: ev.ref_id });
      }
    } catch {
      // Reverte se falhou
      setReactions(prev => ({ ...prev, [key]: cur }));
      message.error('Erro');
    }
  };

  const reportEvent = (ev) => {
    Modal.confirm({
      title: 'Denunciar essa publicação?',
      content: 'Publicações denunciadas por várias pessoas são revisadas pela nossa moderação.',
      okText: 'Denunciar', cancelText: 'Voltar', okType: 'danger',
      onOk: async () => {
        try {
          const targetType = ev.type === 'team_news' ? 'team_news' : ev.type;
          await api.post('/community/reports', { target_type: targetType, target_id: ev.ref_id });
          message.success('Denúncia enviada, valeu');
        } catch { message.error('Erro'); }
      },
    });
  };

  const load = useCallback(async (reset = true) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    if (reset) { setLoading(true); setDone(false); }
    else setLoadingMore(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', compact ? '5' : '20');
      if (!reset && cursorRef.current) params.set('before', cursorRef.current);
      const r = await api.get(`/community/feed?${params}`);
      const newEvents = r.data.events || [];
      const cursor = r.data.next_cursor || null;
      setEvents(prev => reset ? newEvents : [...prev, ...newEvents]);
      cursorRef.current = cursor;
      setNextCursor(cursor);
      if (!cursor) setDone(true);
      // Carrega reactions pros novos events (background)
      fetchReactions(newEvents);
    } catch (e) {
      /* silencia */
    } finally {
      setLoading(false); setLoadingMore(false);
      loadingRef.current = false;
    }
  }, [compact, fetchReactions]);

  useEffect(() => { load(true); /* eslint-disable-next-line */ }, [compact]);

  // Lazy loading via IntersectionObserver: quando o sentinel entra no viewport,
  // dispara load(false). So ativo no modo full (nao compact).
  useEffect(() => {
    if (compact || done) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loadingRef.current && cursorRef.current) {
        load(false);
      }
    }, { rootMargin: '200px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [compact, done, events.length, load]);

  const handleClick = (ev) => {
    if (ev.type === 'new_team') nav('/player/map');
    if (ev.type === 'team_news' && ev.badge) {
      // badge do team_news carrega o slug do time; abre pagina publica
      window.location.href = `/time/${ev.badge}`;
    }
  };

  const goFeed = () => {
    if (onVerMais) onVerMais();
    else nav('/player/feed');
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Title level={5} style={{ margin: 0 }}>
          <FireOutlined style={{ color: '#25D366' }} /> Rolando na comunidade
        </Title>
        {!compact && (
          <Button size="small" icon={<ReloadOutlined />} onClick={() => load(true)} loading={loading}>
            Atualizar
          </Button>
        )}
      </div>

      {loading && events.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : events.length === 0 ? (
        <Card><Empty description="Sem novidades por aqui ainda. Volte mais tarde." /></Card>
      ) : (
        <>
          {events.map((ev, i) => (
            <EventCard key={`${ev.type}-${ev.ref_id}-${i}`} ev={ev}
              onClick={() => handleClick(ev)}
              reaction={reactions[`${ev.type}:${ev.ref_id}`]}
              onLike={() => toggleLike(ev)}
              onReport={() => reportEvent(ev)}
            />
          ))}
          {compact ? (
            <Button block onClick={goFeed}>Ver feed completo</Button>
          ) : (
            <>
              {/* Sentinel invisivel — quando entra no viewport, carrega proxima pagina */}
              <div ref={sentinelRef} style={{ height: 1 }} />
              {loadingMore && (
                <div style={{ textAlign: 'center', padding: 16 }}><Spin size="small" /></div>
              )}
              {done && events.length > 0 && (
                <div style={{ textAlign: 'center', padding: 16, color: '#8c8c8c', fontSize: 12 }}>
                  ✓ Você viu tudo por enquanto
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
